import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, X, Loader2, Inbox, Coins } from "lucide-react";

const STATUS_STYLE = {
  PENDIENTE: { bg: "#F59E0B22", color: "#FBBF24", label: "Pendiente" },
  APROBADA: { bg: "#10B98122", color: "#34D399", label: "Aprobada" },
  RECHAZADA: { bg: "#EF444422", color: "#F87171", label: "Rechazada" },
};

export default function Requests() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("PENDIENTE");
  const [busyId, setBusyId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["credit-requests", filter],
    queryFn: async () => (await api.get(`/admin/credit-requests${filter ? `?status=${filter}` : ""}`)).data,
    refetchInterval: 20000,
  });

  const act = useMutation({
    mutationFn: async ({ id, action }) =>
      (await api.post(`/admin/credit-requests/${id}/${action}`)).data,
    onSuccess: (res, vars) => {
      toast.success(vars.action === "approve"
        ? `Créditos cargados. Nuevo saldo: ${res.after}`
        : "Solicitud rechazada");
      qc.invalidateQueries({ queryKey: ["credit-requests"] });
      setBusyId(null);
    },
    onError: (e) => {
      toast.error(e.response?.data?.detail || "No se pudo procesar la solicitud");
      setBusyId(null);
    },
  });

  const run = (id, action) => { setBusyId(id + action); act.mutate({ id, action }); };
  const rows = data?.requests || [];

  return (
    <Layout>
      <PageHeader title="Solicitudes de Créditos"
        subtitle="Aprueba una solicitud cuando recibas la transferencia y los créditos se cargan solos" />
      <div className="p-8">
        <div className="flex items-center gap-2 mb-5">
          {[["PENDIENTE", "Pendientes"], ["APROBADA", "Aprobadas"], ["RECHAZADA", "Rechazadas"], ["", "Todas"]].map(([v, l]) => (
            <button key={v || "all"} data-testid={`filter-${v || "all"}`} onClick={() => setFilter(v)}
              className={`px-3 py-1.5 rounded-md border text-xs transition-colors ${filter === v ? "bg-[#C2620E] border-[#C2620E] text-white" : "border-[#1E293B] text-slate-300 hover:bg-[#131A2A]"}`}>
              {l}
            </button>
          ))}
          {data?.pending > 0 && (
            <span data-testid="pending-count" className="ml-auto text-xs font-mono px-2 py-1 rounded"
              style={{ backgroundColor: "#F59E0B22", color: "#FBBF24" }}>
              {data.pending} pendiente(s)
            </span>
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
        ) : rows.length === 0 ? (
          <div data-testid="requests-empty" className="flex flex-col items-center justify-center py-20 text-slate-500">
            <Inbox className="w-10 h-10 mb-3 opacity-40" />
            <p className="text-sm">No hay solicitudes en esta vista.</p>
          </div>
        ) : (
          <div className="space-y-3 max-w-4xl" data-testid="requests-list">
            {rows.map((r) => {
              const st = STATUS_STYLE[r.status] || STATUS_STYLE.PENDIENTE;
              return (
                <div key={r.request_id} data-testid={`request-${r.request_id}`}
                  className="rounded-lg border p-4 flex flex-col sm:flex-row sm:items-center gap-4"
                  style={{ backgroundColor: "#111827", borderColor: "#1E293B" }}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-100 font-semibold text-sm">{r.name || "Usuario"}</span>
                      {r.username && <span className="text-slate-500 text-xs">@{r.username}</span>}
                      <span className="text-[10px] px-2 py-0.5 rounded font-mono"
                        style={{ backgroundColor: st.bg, color: st.color }}>{st.label}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1 font-mono">ID {r.telegram_id} · {new Date(r.created_at).toLocaleString("es-MX")}</div>
                  </div>
                  <div className="flex items-center gap-1.5 text-amber-400 font-mono text-sm">
                    <Coins className="w-4 h-4" /> {r.amount} créditos
                    {r.price_mxn ? <span className="text-slate-500">· ${r.price_mxn.toLocaleString()} MXN</span> : null}
                  </div>
                  {r.status === "PENDIENTE" && (
                    <div className="flex items-center gap-2">
                      <Button data-testid={`approve-${r.request_id}`} onClick={() => run(r.request_id, "approve")}
                        disabled={busyId !== null} className="bg-emerald-600 hover:bg-emerald-500 text-white h-9">
                        {busyId === r.request_id + "approve" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Aprobar</>}
                      </Button>
                      <Button data-testid={`reject-${r.request_id}`} onClick={() => run(r.request_id, "reject")}
                        disabled={busyId !== null} variant="outline"
                        className="border-[#334155] bg-transparent text-slate-300 hover:bg-[#1E293B] h-9">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
